import { lazy, memo, Suspense } from 'react';

import { WorkspaceAgentPendingPill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPendingPill';

const ShareLinkMoniker = lazy(async () => {
  const { PrivateWorkspaceAgentPanelMessageShareLinkMoniker } = await import(
    '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentPanelMessageShareLinkMoniker'
  );

  return { default: PrivateWorkspaceAgentPanelMessageShareLinkMoniker };
});

export type PrivateWorkspaceAgentTableShareLinkMonikerProps = {
  shortId: string;
};

/**
 * Agent table cells (and other inline moniker surfaces that reuse the table chip) may mount many cells
 * where no row references a share link. Eagerly importing the message share-link chip would pull its
 * whole chunk — popover preview, open-asset/board navigation, share-settings modal hooks — into the
 * table bundle even when zero share links render.
 *
 * Message prose imports {@link PrivateWorkspaceAgentPanelMessageShareLinkMoniker} directly when
 * `@[shareLink:…]` appears in a turn; this wrapper is the only table moniker that code-splits via
 * `React.lazy` (see {@link PrivateWorkspaceAgentTableMoniker} for the eager path). `Suspense` shows
 * {@link WorkspaceAgentPendingPill} while the chunk loads, matching in-message pending UX.
 */
export const PrivateWorkspaceAgentTableShareLinkMoniker = memo(
  ({ shortId }: PrivateWorkspaceAgentTableShareLinkMonikerProps) => (
    <Suspense fallback={<WorkspaceAgentPendingPill label="Share link" />}>
      <ShareLinkMoniker shortId={shortId} />
    </Suspense>
  ),
);

PrivateWorkspaceAgentTableShareLinkMoniker.displayName = 'PrivateWorkspaceAgentTableShareLinkMoniker';
